// Step 27 follow-up: review every approval + rejection before running
// the eval, so the editor can spot weird/wrong labels and undo them.
// One modal, two tabs (Approvals / Rejections), grouped by tbtv_id,
// per-row "Undo" button.
//
// Loads the full label set on open (capped at 5000 each — way more than
// any realistic editorial gold set). Refetches after each undo so the
// list always reflects server truth.

import { useState, useEffect, useMemo } from 'react'
import { X, CheckCircle, XCircle, Trash2, Loader2, Filter, RefreshCw } from 'lucide-react'
import { useHighlightsApi } from './useHighlightsApi'

function fmtMs(ms) {
  if (ms == null) return '—'
  const totalS = Math.floor(ms / 1000)
  const h = Math.floor(totalS / 3600)
  const m = Math.floor((totalS % 3600) / 60)
  const s = totalS % 60
  if (h > 0) return `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`
  return `${m}:${String(s).padStart(2, '0')}`
}
function fmtAgo(iso) {
  if (!iso) return '—'
  const then = new Date(iso.replace(' ', 'T') + 'Z').getTime()
  if (!Number.isFinite(then)) return iso
  const deltaS = Math.floor((Date.now() - then) / 1000)
  if (deltaS < 60) return `${deltaS}s ago`
  if (deltaS < 3600) return `${Math.floor(deltaS / 60)}m ago`
  if (deltaS < 86400) return `${Math.floor(deltaS / 3600)}h ago`
  return `${Math.floor(deltaS / 86400)}d ago`
}

export default function ReviewLabelsModal({ onClose }) {
  const api = useHighlightsApi()
  const [tab, setTab] = useState('approvals')
  const [approvals, setApprovals] = useState(null)
  const [drops, setDrops] = useState(null)
  const [err, setErr] = useState(null)
  const [loading, setLoading] = useState(false)
  const [filterTbtvId, setFilterTbtvId] = useState('')
  const [busyUndoId, setBusyUndoId] = useState(null)

  const refresh = async () => {
    setLoading(true)
    setErr(null)
    try {
      const [a, d] = await Promise.all([
        api.listApprovedMoments(null, 5000),
        api.listDroppedMoments(null, 5000),
      ])
      setApprovals(a?.approvals || [])
      setDrops(d?.drops || [])
    } catch (e) {
      setErr(e?.message || String(e))
    } finally {
      setLoading(false)
    }
  }
  useEffect(() => { refresh() }, [])
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose?.() }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  const rows = tab === 'approvals' ? approvals : drops
  const filtered = useMemo(() => {
    if (!rows) return []
    const f = filterTbtvId.trim().toLowerCase()
    if (!f) return rows
    return rows.filter(r => (r.tbtv_id || '').toLowerCase().includes(f))
  }, [rows, filterTbtvId])

  // Distinct tbtv_ids for the filter dropdown.
  const distinctVideos = useMemo(() => {
    if (!rows) return []
    const set = new Map()
    for (const r of rows) {
      set.set(r.tbtv_id, (set.get(r.tbtv_id) || 0) + 1)
    }
    return [...set.entries()].sort((a, b) => b[1] - a[1])
  }, [rows])

  async function undoOne(id) {
    if (busyUndoId) return
    const label = tab === 'approvals' ? 'approval' : 'rejection'
    if (!window.confirm(`Undo this ${label}?\n\nThe ${label} record will be removed. Already-deleted key_moments rows (from rejections) are NOT restored — you'd need to re-run TBTV-AI to regenerate them.`)) return
    setBusyUndoId(id)
    try {
      if (tab === 'approvals') {
        await api.undoApprovedMoment(id)
      } else {
        await api.undoDroppedMoment(id)
      }
      await refresh()
    } catch (e) {
      window.alert(`Undo failed: ${e?.message || e}`)
    } finally {
      setBusyUndoId(null)
    }
  }

  return (
    <div
      onClick={(e) => { if (e.target === e.currentTarget) onClose?.() }}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999,
      }}>
      <div style={{
        background: '#111', border: '1px solid #2a2a2a', borderRadius: 8,
        width: 'min(960px, 96vw)', maxHeight: '90vh', display: 'flex', flexDirection: 'column',
      }}>
        {/* Header */}
        <div style={{ padding: '12px 16px', borderBottom: '1px solid #2a2a2a', display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 14, fontWeight: 600, color: '#bfdbfe' }}>Review labels</div>
            <div style={{ fontSize: 11, color: '#888', marginTop: 2 }}>
              Editor-applied approvals + rejections across the catalog. Undo to remove a label.
              Rejections deleted the underlying key_moments row — undo does NOT restore it (re-run TBTV-AI to regenerate).
            </div>
          </div>
          <button onClick={refresh} disabled={loading}
            title="Refresh from D1"
            style={{ padding: '4px 10px', fontSize: 11, background: 'transparent', border: '1px solid #333', borderRadius: 4, color: '#888', cursor: loading ? 'default' : 'pointer', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
            <RefreshCw size={11} className={loading ? 'spin' : ''} /> Refresh
          </button>
          <button onClick={onClose}
            style={{ background: 'transparent', border: 'none', color: '#888', cursor: 'pointer', padding: 4 }}>
            <X size={18} />
          </button>
        </div>

        {/* Tabs */}
        <div style={{ display: 'flex', borderBottom: '1px solid #2a2a2a' }}>
          <TabButton
            active={tab === 'approvals'} onClick={() => setTab('approvals')}
            color="#4ade80" icon={<CheckCircle size={12} />}
            label={`Approvals (${approvals?.length ?? '—'})`}
          />
          <TabButton
            active={tab === 'rejections'} onClick={() => setTab('rejections')}
            color="#fca5a5" icon={<XCircle size={12} />}
            label={`Rejections (${drops?.length ?? '—'})`}
          />
        </div>

        {/* Filter strip */}
        <div style={{ padding: '8px 16px', borderBottom: '1px solid #1f1f1f', display: 'flex', gap: 8, alignItems: 'center', fontSize: 11 }}>
          <Filter size={12} style={{ color: '#888' }} />
          <select
            value={filterTbtvId}
            onChange={(e) => setFilterTbtvId(e.target.value)}
            style={{ background: '#0d1622', border: '1px solid #1e293b', borderRadius: 4, color: '#ddd', padding: '3px 8px', fontSize: 11, fontFamily: 'monospace', minWidth: 280 }}>
            <option value="">All videos ({distinctVideos.length})</option>
            {distinctVideos.map(([id, n]) => (
              <option key={id} value={id}>{id} ({n})</option>
            ))}
          </select>
          <span style={{ color: '#888' }}>
            Showing {filtered.length} of {rows?.length ?? '—'}
          </span>
        </div>

        {/* Body */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '8px 16px' }}>
          {err && <div style={{ color: '#dc2626', padding: 12 }}>{err}</div>}
          {loading && !rows && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#888', padding: 24, justifyContent: 'center' }}>
              <Loader2 size={14} className="spin" /> Loading…
            </div>
          )}
          {rows && filtered.length === 0 && !loading && (
            <div style={{ padding: 32, textAlign: 'center', color: '#666', fontSize: 12 }}>
              No {tab} {filterTbtvId ? `for ${filterTbtvId}` : 'yet'}.
            </div>
          )}
          {filtered.length > 0 && (
            <table style={{ width: '100%', fontSize: 12, color: '#ddd', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ color: '#888', fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                  <th style={{ textAlign: 'left', padding: '6px 8px', borderBottom: '1px solid #1e293b' }}>Video</th>
                  <th style={{ textAlign: 'left', padding: '6px 8px', borderBottom: '1px solid #1e293b', width: 80 }}>Time</th>
                  <th style={{ textAlign: 'left', padding: '6px 8px', borderBottom: '1px solid #1e293b', width: 90 }}>Type</th>
                  <th style={{ textAlign: 'left', padding: '6px 8px', borderBottom: '1px solid #1e293b' }}>Title</th>
                  <th style={{ textAlign: 'left', padding: '6px 8px', borderBottom: '1px solid #1e293b', width: 110 }}>{tab === 'approvals' ? 'Source' : 'Reason'}</th>
                  <th style={{ textAlign: 'left', padding: '6px 8px', borderBottom: '1px solid #1e293b', width: 80 }}>When</th>
                  <th style={{ textAlign: 'right', padding: '6px 8px', borderBottom: '1px solid #1e293b', width: 80 }}></th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(r => (
                  <tr key={r.id} style={{ borderBottom: '1px solid #161616' }}>
                    <td style={{ padding: '6px 8px', fontFamily: 'monospace', fontSize: 10, color: '#aaa' }}>{r.tbtv_id}</td>
                    <td style={{ padding: '6px 8px', fontFamily: 'monospace', color: '#7dd3fc' }}>{fmtMs(r.start_ms)}</td>
                    <td style={{ padding: '6px 8px', color: '#aaa' }}>{r.moment_type || '—'}</td>
                    <td style={{ padding: '6px 8px', color: '#ddd' }}>{r.title || '(untitled)'}</td>
                    <td style={{ padding: '6px 8px', fontSize: 11 }}>
                      {tab === 'approvals'
                        ? <SourcePill source={r.source} detector={r.detector} />
                        : <ReasonPill category={r.reason_category} note={r.reason_note} />}
                    </td>
                    <td style={{ padding: '6px 8px', color: '#888', fontSize: 11 }}>{fmtAgo(r.approved_at || r.dropped_at)}</td>
                    <td style={{ padding: '6px 8px', textAlign: 'right' }}>
                      <button onClick={() => undoOne(r.id)} disabled={busyUndoId === r.id}
                        title="Remove this label"
                        style={{
                          padding: '3px 8px', fontSize: 10,
                          background: 'transparent', border: '1px solid #333', borderRadius: 4,
                          color: '#888', cursor: busyUndoId === r.id ? 'default' : 'pointer',
                          display: 'inline-flex', alignItems: 'center', gap: 3,
                        }}>
                        {busyUndoId === r.id ? <Loader2 size={10} className="spin" /> : <Trash2 size={10} />}
                        Undo
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  )
}

function TabButton({ active, onClick, color, icon, label }) {
  return (
    <button onClick={onClick} style={{
      flex: 1, padding: '10px 14px', fontSize: 12,
      background: active ? 'rgba(255,255,255,0.04)' : 'transparent',
      border: 'none', borderBottom: active ? `2px solid ${color}` : '2px solid transparent',
      color: active ? color : '#888', cursor: 'pointer',
      display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6,
      fontWeight: active ? 600 : 400,
    }}>
      {icon} {label}
    </button>
  )
}

function SourcePill({ source, detector }) {
  if (!source) return <span style={{ color: '#666' }}>—</span>
  const isAi = source === 'tbtv_ai'
  const isMux = source === 'mux'
  const isManual = source === 'editor_manual'
  const color = isAi ? '#a78bfa' : (isMux ? '#fb923c' : (isManual ? '#4ade80' : '#888'))
  const label = isAi ? `ai${detector ? '·' + detector : ''}` : (isMux ? 'mux' : (isManual ? 'manual' : source))
  return (
    <span style={{
      fontSize: 10, padding: '1px 6px', borderRadius: 3,
      background: `${color}22`, color, fontWeight: 600,
    }}>{label}</span>
  )
}

function ReasonPill({ category, note }) {
  if (!category) return <span style={{ color: '#666' }}>—</span>
  const colorMap = {
    hallucinated: '#dc2626',
    recap: '#f59e0b',
    wrong_type: '#a78bfa',
    marginal: '#888',
    wrong_window: '#7dd3fc',
    quality: '#666',
    other: '#888',
  }
  const color = colorMap[category] || '#888'
  return (
    <span title={note || category}
      style={{
        fontSize: 10, padding: '1px 6px', borderRadius: 3,
        background: `${color}22`, color, fontWeight: 600,
      }}>
      {category}
    </span>
  )
}
